/**
 * `#intelFocus` — "Focus on", the keyboard way into the one thing the panes could only do
 * with a mouse (client round 3 Oct 2026).
 *
 * It sits under the level switch, inside the map-controls card, on both UC1 and UC2, and
 * lists the units of the level the pane is on: "All watersheds" / "All countries" first,
 * then every unit by name with its own class in brackets, so the list is also a reading of
 * the programme rather than eleven bare names.
 *
 * Choosing a unit **is** a map click plus a fit: the pane's `useUnitLink.pick` writes
 * `selectedUnit` and `intel.unit`, draws the same `intel:{uc}:selected` outline a click
 * draws, and moves the view to the unit. Everything downstream — the chip outline, the
 * tiles, the cards, the per-unit charts — follows from those two store fields and needs
 * nothing of its own. Choosing the "All" option clears the selection the same way round.
 *
 * The select **mirrors** the selection rather than owning it, so a map click moves it too.
 * A unit selected at the other level (a watershed while the switch is on Country) is in
 * neither list; the select then shows the "All" option rather than an empty box, which is
 * the truth about this level's own focus.
 */
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { IntelUnitRow } from '../../lib/api';

export interface IntelFocusProps {
  /** The rows of the level the pane is on — the options, in their own order. */
  rows: IntelUnitRow[];
  /** `'watershed'` or `'admin0'`, which is only the wording of the "All" option. */
  level: string;
  /** `intel.unit` — what the select mirrors. */
  selected: string | null;
  /** A unit's class, already localised; printed in brackets after its name. */
  classLabel: (row: IntelUnitRow) => string;
  /** Adopt that unit, exactly as a map click would, and fit the map to it. */
  onFocus: (unitId: string, name: string) => void;
  /** Back to the whole programme. */
  onClear: () => void;
}

export function IntelFocus({
  rows,
  level,
  selected,
  classLabel,
  onFocus,
  onClear,
}: IntelFocusProps) {
  const { t } = useTranslation();
  // Sorted by name, with the collator rather than raw code points: "Élevage" belongs
  // beside "Est", not after "Zou".
  const units = useMemo(() => [...rows].sort((a, b) => a.name.localeCompare(b.name)), [rows]);
  const value = selected && units.some((r) => r.unit_id === selected) ? selected : '';
  const allLabel = t(level === 'admin0' ? 'intel.focusAllCountries' : 'intel.focusAllWatersheds');

  return (
    <label className="intel-focus" data-testid="intel-focus">
      <span>{t('intel.focusLabel')}</span>
      <select
        id="intelFocus"
        aria-label={t('intel.focusLabel')}
        value={value}
        onChange={(e) => {
          const id = e.target.value;
          if (!id) {
            onClear();
            return;
          }
          onFocus(id, units.find((r) => r.unit_id === id)?.name ?? id);
        }}
      >
        <option value="">{allLabel}</option>
        {units.map((row) => (
          <option key={row.unit_id} value={row.unit_id}>
            {`${row.name} (${classLabel(row)})`}
          </option>
        ))}
      </select>
    </label>
  );
}

export default IntelFocus;
